/**
 * Application configuration
 * This file contains configurable settings for the application
 */

export interface AwardCategory {
  value: string;
  label: string;
  labelChi: string;
  description?: string;
  descriptionChi?: string;
}

export const APP_CONFIG = {
  // Current application year (used for application IDs and routing)
  APPLICATION_YEAR: '2025',

  // Application ID prefix (should match APPLICATION_YEAR)
  APPLICATION_ID_PREFIX: '2025',

  // Email configuration
  EMAIL: {
    AMPLIFY_FROM_EMAIL: 'smartcityawards@etnet.com.hk',
    SUBJECT: '2025/26 智慧生活夥伴大獎 - Application Confirmation 申請確認',
    CONTACT_PHONE: '(+852) 2880 2978',
    CONTACT_EMAIL: 'smartcityawards@etnet.com.hk'
  },

  // Award program details
  AWARD_PROGRAM: {
    NAME_ENG: 'Smart Living Partnership Awards 2025/26',
    NAME_CHI: '2025/26智慧生活夥伴大獎',
    ORGANIZING_COMMITTEE: 'Smart Living Partnership Awards Organising Committee'
  },

  // Award categories for 2025
  AWARD_CATEGORIES: [
    {
      value: 'arts',
      label: 'Arts and Culture',
      labelChi: '藝術與文化',
      description: 'Innovative solutions in arts, culture, and creative industries',
      descriptionChi: '藝術、文化和創意產業的創新解決方案'
    },
    {
      value: 'epay',
      label: 'Consumer Financial Solution (e-Payment)',
      labelChi: '消費者財務方案（電子支付）',
      description: 'Digital payment and financial technology solutions',
      descriptionChi: '數字支付和金融科技解決方案'
    },
    {
      value: 'edtech',
      label: 'Educational Technology',
      labelChi: '教育技術',
      description: 'Technology solutions for education and learning',
      descriptionChi: '教育和學習的技術解決方案'
    },
    {
      value: 'esgproduct',
      label: 'ESG Product',
      labelChi: 'ESG 產品',
      description: 'Environmental, Social, and Governance focused products',
      descriptionChi: '環境、社會和治理導向的產品'
    },
    {
      value: 'esgproject',
      label: 'ESG Project',
      labelChi: 'ESG 項目',
      description: 'Environmental, Social, and Governance focused projects',
      descriptionChi: '環境、社會和治理導向的項目'
    },
    {
      value: 'esgsolution',
      label: 'ESG Solution',
      labelChi: 'ESG 解決方案',
      description: 'Environmental, Social, and Governance focused solutions',
      descriptionChi: '環境、社會和治理導向的解決方案'
    },
    {
      value: 'geron',
      label: 'Geron Technology',
      labelChi: '樂齡科技',
      description: 'Technology solutions for elderly care and aging population',
      descriptionChi: '為長者護理和人口老齡化提供的技術解決方案'
    },
    {
      value: 'health',
      label: 'Health Technology',
      labelChi: '健康科技',
      description: 'Healthcare and medical technology innovations',
      descriptionChi: '醫療保健和醫療技術創新'
    },
    {
      value: 'marTech',
      label: 'Marketing Technology (Smart Retail / e-Commerce)',
      labelChi: '行銷科技（智慧零售 / 電子商務）',
      description: 'Marketing technology, smart retail, and e-commerce solutions',
      descriptionChi: '行銷科技、智慧零售和電子商務解決方案'
    },
    {
      value: 'proTech',
      label: 'Property Technology',
      labelChi: '房地產科技',
      description: 'Real estate and property management technology',
      descriptionChi: '房地產和物業管理技術'
    },
    {
      value: 'business',
      label: 'Small and Medium Business (Tourism / Human Resources / Administration)',
      labelChi: '中小型企業（旅遊 / 人力資源 / 行政）',
      description: 'Solutions for SMEs in tourism, HR, and administration',
      descriptionChi: '為中小型企業在旅遊、人力資源和行政方面提供的解決方案'
    },
    {
      value: 'enviro',
      label: 'Smart Building / Environment Technology / Green Technology',
      labelChi: '智能建設 / 環境科技 / 綠色科技',
      description: 'Smart building, environmental, and green technology solutions',
      descriptionChi: '智能建築、環境和綠色科技解決方案'
    },
    {
      value: 'gov',
      label: 'Smart Government',
      labelChi: '智慧政府',
      description: 'Government and public sector technology solutions',
      descriptionChi: '政府和公共部門技術解決方案'
    },
    {
      value: 'mobility',
      label: 'Smart Mobility',
      labelChi: '智慧出行',
      description: 'Transportation and mobility technology solutions',
      descriptionChi: '交通和出行技術解決方案'
    },
    {
      value: 'management',
      label: 'Smart Workforce Management / Smart People ',
      labelChi: '智慧勞動管理 / 智慧市民',
      description: 'Workforce and human resource management technology',
      descriptionChi: '勞動力和人力資源管理技術'
    },
    {
      value: 'wearable',
      label: 'Wearable / Internet of Things (IOT) / Robotics',
      labelChi: '智慧衣服 / 物聯網 / 機械人技術',
      description: 'Wearable devices, IoT, and robotics technology',
      descriptionChi: '可穿戴設備、物聯網和機械人技術'
    },
    {
      value: 'metaverse',
      label: 'Web3 / Metaverse',
      labelChi: 'Web3 / 元宇宙',
      description: 'Web3, blockchain, and metaverse technologies',
      descriptionChi: 'Web3、區塊鏈和元宇宙技術'
    }
  ] as const
} as const;

// Type for the configuration
export type AppConfig = typeof APP_CONFIG;

// Utility function to get the current application year
export function getCurrentApplicationYear(): string {
  return APP_CONFIG.APPLICATION_YEAR;
}

// Utility function to get the application ID prefix
export function getApplicationIdPrefix(): string {
  return APP_CONFIG.APPLICATION_ID_PREFIX;
}

// Utility function to check if a given year matches the current application year
export function isCurrentApplicationYear(year: string): boolean {
  return year === APP_CONFIG.APPLICATION_YEAR;
}

// Award category utility functions
export function getAwardCategoryLabel(value: string): string {
  const found = APP_CONFIG.AWARD_CATEGORIES.find(cat => cat.value === value);
  return found ? `${found.label} / ${found.labelChi}` : value;
}

export function getAwardCategoryByValue(value: string): AwardCategory | undefined {
  return APP_CONFIG.AWARD_CATEGORIES.find(cat => cat.value === value);
}

export function getAwardCategoryLabels(): { value: string; label: string; labelChi: string }[] {
  return APP_CONFIG.AWARD_CATEGORIES.map(cat => ({
    value: cat.value,
    label: cat.label,
    labelChi: cat.labelChi
  }));
}

// For backward compatibility
export const AWARD_CATEGORIES = APP_CONFIG.AWARD_CATEGORIES;
export const awardCategories = APP_CONFIG.AWARD_CATEGORIES;